

import React, { useEffect, useRef, useState } from 'react';
import {
  Box, TextField, FormControl, Select, MenuItem, Checkbox, ListItemText,
  Divider, Typography, Button
} from '@mui/material';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import SearchIcon from '@mui/icons-material/Search';
import axios from 'axios';

// import REPORTS_URL from '../config';

// Syncfusion QueryBuilder imports
import { QueryBuilderComponent } from '@syncfusion/ej2-react-querybuilder';
import { ButtonComponent } from '@syncfusion/ej2-react-buttons';
import '@syncfusion/ej2-base/styles/material.css';
import '@syncfusion/ej2-buttons/styles/material.css';
import '@syncfusion/ej2-popups/styles/material.css';
import '@syncfusion/ej2-splitbuttons/styles/material.css';
import '@syncfusion/ej2-inputs/styles/material.css';
import '@syncfusion/ej2-lists/styles/material.css';
import '@syncfusion/ej2-dropdowns/styles/material.css';
import '@syncfusion/ej2-calendars/styles/material.css';
import '@syncfusion/ej2-navigations/styles/material.css';
import '@syncfusion/ej2-querybuilder/styles/material.css';

const DEFAULT_RULE = { condition: 'and', rules: [] };

const AssignementFilters = ({
  searchTerm, setSearchTerm, statusFilters, setStatusFilters,
  departmentFilters, setDepartmentFilters, assignedFilters, setAssignedFilters,
  dateRange, setDateRange, dropdownOpen, setDropdownOpen,
  filterRule, setFilterRule,
  showFilters, setShowFilters
}) => {
  const [statusOptions, setStatusOptions] = useState(['Passed', 'Failed', 'Missed']);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [assignedOptions, setAssignedOptions] = useState([]);
  const [localSearch, setLocalSearch] = useState(searchTerm);
  const dropdownRef = useRef(null);

  // QueryBuilder state
  const qbRef = useRef(null);
  const currentRuleRef = useRef(filterRule || DEFAULT_RULE);

  // Columns for QueryBuilder (field names must match report object keys)

  const stringOperators = [
    { value: 'contains', key: 'Contains' },
    { value: 'equal', key: 'Equal' },
    { value: 'notequal', key: 'Not Equal' },
    { value: 'startswith', key: 'Starts With' },
    { value: 'endswith', key: 'Ends With' },
    { value: 'isempty', key: 'Is Empty' },
    { value: 'isnotempty', key: 'Is Not Empty' }
  ];

  const numberOperators = [
    { value: 'equal', key: 'Equal' },
    { value: 'notequal', key: 'Not Equal' },
    { value: 'greaterthan', key: 'Greater Than' },
    { value: 'greaterthanorequal', key: 'Greater Than Or Equal' },
    { value: 'lessthan', key: 'Less Than' },
    { value: 'lessthanorequal', key: 'Less Than Or Equal' },
    { value: 'isempty', key: 'Is Empty' },
    { value: 'isnotempty', key: 'Is Not Empty' }
  ];

  const dateOperators = [
    { value: 'equal', key: 'Equal' },
    { value: 'notequal', key: 'Not Equal' },
    { value: 'greaterthan', key: 'After' },
    { value: 'lessthan', key: 'Before' },
    { value: 'greaterthanorequal', key: 'On or After' },
    { value: 'lessthanorequal', key: 'On or Before' },
    { value: 'between', key: 'Between' },
    { value: 'isempty', key: 'Is Empty' },
    { value: 'isnotempty', key: 'Is Not Empty' }
  ];

  // Use a calendar plus separate hour, minute, and AM/PM selectors instead of
  // a predefined time-interval list.
  const dateTimeTemplate = {
    create: () => document.createElement('div'),

    write: (args) => {
      const elements = args.elements.length ? Array.from(args.elements) : [args.elements];
      const values = Array.isArray(args.values) ? args.values : [args.values];

      elements.forEach((element, index) => {
        const value = String(values[index] || '');
        const twelveHourMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})\s+(AM|PM)$/i);
        const [, year = '', month = '', day = '', hour = '', minute = '', period = 'AM'] = twelveHourMatch || [];

        element.replaceChildren();
        Object.assign(element.style, {
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          flexWrap: 'wrap',
          width: '180px',
          minWidth: '0'
        });

        const dateInput = document.createElement('input');
        dateInput.type = 'date';
        dateInput.value = year ? `${year}-${month}-${day}` : '';
        dateInput.setAttribute('aria-label', 'Select date');

        const hourSelect = document.createElement('select');
        const minuteSelect = document.createElement('select');
        const periodSelect = document.createElement('select');
        hourSelect.setAttribute('aria-label', 'Select hour');
        minuteSelect.setAttribute('aria-label', 'Select minute');
        periodSelect.setAttribute('aria-label', 'Select AM or PM');

        hourSelect.innerHTML = '<option value="">HH</option>' +
          Array.from({ length: 12 }, (_, hourIndex) => {
            const hourValue = String(hourIndex + 1).padStart(2, '0');
            return `<option value="${hourValue}">${hourValue}</option>`;
          }).join('');
        minuteSelect.innerHTML = '<option value="">MM</option>' +
          Array.from({ length: 60 }, (_, minuteIndex) => {
            const minuteValue = String(minuteIndex).padStart(2, '0');
            return `<option value="${minuteValue}">${minuteValue}</option>`;
          }).join('');
        periodSelect.innerHTML = '<option value="AM">AM</option><option value="PM">PM</option>';

        hourSelect.value = hour ? hour.padStart(2, '0') : '';
        minuteSelect.value = minute;
        periodSelect.value = period.toUpperCase();

        const notifyDateTimeChange = () => {
          if (!dateInput.value || !hourSelect.value || !minuteSelect.value) return;

          qbRef.current?.notifyChange(
            `${dateInput.value} ${hourSelect.value}:${minuteSelect.value} ${periodSelect.value}`,
            dateInput
          );
        };

        [dateInput, hourSelect, minuteSelect, periodSelect].forEach((control) => {
          control.addEventListener('change', notifyDateTimeChange);
          Object.assign(control.style, {
            height: '36px',
            boxSizing: 'border-box',
            border: '1px solid #9e9e9e',
            borderRadius: '4px',
            background: '#fff',
            color: '#212121',
            fontSize: '14px',
            padding: '0 6px',
            flexShrink: '0'
          });
        });

        dateInput.style.width = '176px';
        dateInput.style.flexBasis = '176px';
        hourSelect.style.width = '48px';
        minuteSelect.style.width = '52px';
        periodSelect.style.width = '54px';

        const timeSeparator = document.createElement('span');
        timeSeparator.textContent = ':';
        timeSeparator.style.fontWeight = '600';
        timeSeparator.style.color = '#555';

        element.append(dateInput, hourSelect, timeSeparator, minuteSelect, periodSelect);
      });
    }
  };


  const queryBuilderColumns = [
    {
      field: "employeeName",
      label: "Employee Name",
      type: "string",
      operators: stringOperators
    },
    {
      field: "departmentName",
      label: "Department",
      type: "string",
      operators: stringOperators
    },
    {
      field: "assignmentNumber",
      label: "Assessment Number",
      type: "string",
      operators: stringOperators
    },
    {
      field: "assignedBy",
      label: "Assigned By",
      type: "string",
      operators: stringOperators
    },
    {
      field: "scheduledDate",
      label: "Schedule Date & Time",
      type: "date",
      format: "yyyy-MM-dd hh:mm a",
      operators: dateOperators,
      template: dateTimeTemplate
    },
    {
      field: "slotStart",
      label: "Slot Start",
      type: "date",
      format: "yyyy-MM-dd hh:mm a",
      operators: dateOperators,
      template: dateTimeTemplate
    },
    {
      field: "slotEnd",
      label: "Slot End",
      type: "date",
      format: "yyyy-MM-dd hh:mm a",
      operators: dateOperators,
      template: dateTimeTemplate
    },
    {
      field: "status",
      label: "Status",
      type: "string",
      operators: stringOperators
    }
  ];

  const applyFilters = () => {
    const rule = currentRuleRef.current || DEFAULT_RULE;
    setFilterRule(rule);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterRule(null);
    if (qbRef.current) {
      qbRef.current.setRules(DEFAULT_RULE);
      currentRuleRef.current = DEFAULT_RULE;
    }
  };

  useEffect(() => {

    setLocalSearch(searchTerm);

  }, [searchTerm]);
  useEffect(() => {

    const timer = setTimeout(() => {

      setSearchTerm(localSearch);

    }, 500);

    return () => clearTimeout(timer);

  }, [localSearch]);

  // Existing useEffect for click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setDropdownOpen(false);
    };
    if (dropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  // Fetch filter options
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await axios.get(`/reports/filter-options/`, { withCredentials: true });
        const data = response.data;
        setDepartmentOptions(Object.entries(data.departments || {}).map(([id, name]) => ({ id, name })));
        setAssignedOptions(Object.values(data.assignment_types || []));
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };
    fetchOptions();
  }, []);

  const handleStatusChange = (status) =>
    setStatusFilters(prev => prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]);

  const handleDepartmentChange = (id) =>
    setDepartmentFilters(prev => prev.includes(id) ? prev.filter(d => d !== id) : [...prev, id]);

  const handleAssignedChange = (assigned) =>
    setAssignedFilters(prev => prev.includes(assigned) ? prev.filter(a => a !== assigned) : [...prev, assigned]);

  const handleDateChange = (e) => setDateRange({ ...dateRange, [e.target.name]: e.target.value });

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1, flexWrap: 'wrap' }}>
          {/* Search Bar */}
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search by Employee Name, Assessment Number, Assigned By,Schedule Date,slot details...."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            sx={{
              minWidth: 280,
              maxWidth: 450,
              flexGrow: 1,
              '& .MuiOutlinedInput-root': {
                height: '40px',
                fontSize: '0.875rem',
                background: 'white',
                '& fieldset': { borderColor: 'black' },
                '&:hover fieldset': { borderColor: 'black' },
                '&.Mui-focused fieldset': { borderColor: 'black' }
              }
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "text.secondary", mr: 1, fontSize: "1.2rem" }} />,
            }}
          />

          {/* Enable Filters Button */}
          <button
            className="btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Close Filters' : 'Enable Filters'}
          </button>


        </Box>
      </Box>

      {/* Syncfusion QueryBuilder (conditionally visible) */}
      {showFilters && (
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #ddd', mb: 2 }}>
          <QueryBuilderComponent
            ref={qbRef}
            dataSource={[]}
            columns={queryBuilderColumns}
            rule={filterRule || DEFAULT_RULE}
            ruleChange={(args) => { currentRuleRef.current = args.rule; }}
            created={() => {
              const activeRule = filterRule || DEFAULT_RULE;
              currentRuleRef.current = activeRule;
              if (qbRef.current) qbRef.current.setRules(activeRule);
            }}
            showButtons={false}
            summaryView={false}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <ButtonComponent cssClass="btn-negative" onClick={clearAllFilters}>
              Clear Filters
            </ButtonComponent>
            <ButtonComponent cssClass="btn" onClick={applyFilters}>
              Apply Filters
            </ButtonComponent>
          </Box>
        </Box>
      )}
    </>
  );
};

export default AssignementFilters;
